import React from "react";
import { Chip } from "@mui/material";
import {
  CheckCircle,
  Schedule,
  Warning,
  Error,
  Send,
  Build,
  Visibility,
  AccountBalance,
  Assignment,
  Cancel,
} from "@mui/icons-material";

// Submission Status Chips
export const SubmissionStatusChip = ({ status, size = "small" }) => {
  const statusConfig = {
    pending: {
      color: "default",
      label: "Pending",
      icon: <Schedule fontSize="small" />,
    },
    in_review: {
      color: "info",
      label: "In Review",
      icon: <Visibility fontSize="small" />,
    },
    corrections_needed: {
      color: "warning",
      label: "Corrections Needed",
      icon: <Warning fontSize="small" />,
    },
    approved: {
      color: "success",
      label: "Approved",
      icon: <CheckCircle fontSize="small" />,
    },
    submitted_to_city: {
      color: "primary",
      label: "Submitted to City",
      icon: <Send fontSize="small" />,
    },
    cancelled: {
      color: "error",
      label: "Cancelled",
      icon: <Cancel fontSize="small" />,
    },
  };

  const config = statusConfig[status] || {
    color: "default",
    label: status?.replace("_", " ") || "Unknown",
    icon: null,
  };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      variant="filled"
    />
  );
};

// Inspection Status Chips
export const InspectionStatusChip = ({ status, size = "small" }) => {
  const statusConfig = {
    pending: {
      color: "default",
      label: "Pending",
      icon: <Schedule fontSize="small" />,
    },
    submitted: {
      color: "info",
      label: "Submitted",
      icon: <Assignment fontSize="small" />,
    },
    pre_approved: {
      color: "warning",
      label: "Pre-Approved",
      icon: <CheckCircle fontSize="small" />,
    },
    approved: {
      color: "primary",
      label: "Approved",
      icon: <CheckCircle fontSize="small" />,
    },
    reported: {
      color: "secondary",
      label: "Reported",
      icon: <Send fontSize="small" />,
    },
    passed: {
      color: "success",
      label: "Passed",
      icon: <CheckCircle fontSize="small" />,
    },
    failed: {
      color: "error",
      label: "Failed",
      icon: <Error fontSize="small" />,
    },
    partially_approved: {
      color: "warning",
      label: "Partially Approved",
      icon: <Warning fontSize="small" />,
    },
    rejected: {
      color: "error",
      label: "Rejected",
      icon: <Cancel fontSize="small" />,
    },
  };

  const config = statusConfig[status] || {
    color: "default",
    label: status?.replace("_", " ") || "Unknown",
    icon: null,
  };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      variant="filled"
    />
  );
};

// Payout Status Chips
export const PayoutStatusChip = ({ status, size = "small" }) => {
  const statusConfig = {
    pending: {
      color: "warning",
      label: "Pending",
      icon: <Schedule fontSize="small" />,
    },
    processing: {
      color: "info",
      label: "Processing",
      icon: <Build fontSize="small" />,
    },
    paid: {
      color: "success",
      label: "Paid",
      icon: <CheckCircle fontSize="small" />,
    },
    failed: {
      color: "error",
      label: "Failed",
      icon: <Error fontSize="small" />,
    },
  };

  const config = statusConfig[status] || {
    color: "default",
    label: status?.replace("_", " ") || "Unknown",
    icon: null,
  };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      variant="filled"
    />
  );
};

// Page Review Status Chips
export const PageStatusChip = ({ status, size = "small" }) => {
  const statusConfig = {
    not_reviewed: {
      color: "default",
      label: "Not Reviewed",
      icon: <Schedule fontSize="small" />,
    },
    compliant: {
      color: "success",
      label: "Compliant",
      icon: <CheckCircle fontSize="small" />,
    },
    needs_correction: {
      color: "error",
      label: "Needs Correction",
      icon: <Error fontSize="small" />,
    },
    not_applicable: {
      color: "default",
      label: "N/A",
      icon: null,
    },
  };

  const config = statusConfig[status] || {
    color: "default",
    label: status?.replace("_", " ") || "Unknown",
    icon: null,
  };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      variant={status === "not_applicable" ? "outlined" : "filled"}
    />
  );
};

// Generic Status Chip factory
export const StatusChip = ({
  status,
  type = "submission",
  size = "small",
  variant = "filled",
}) => {
  const chipComponents = {
    submission: SubmissionStatusChip,
    inspection: InspectionStatusChip,
    payout: PayoutStatusChip,
    page: PageStatusChip,
  };

  const ChipComponent = chipComponents[type] || SubmissionStatusChip;

  return <ChipComponent status={status} size={size} variant={variant} />;
};

export default StatusChip;
